import { api } from './client'
import type { components } from './generated/schema'
import { emit } from '../platform/analytics/analytics'

/**
 * Tell the backend what Firebase now holds.
 *
 * MFA lives in Firebase this phase, but the backend keeps its own record —
 * `GET /mfa/status` is what iOS reads, and without this a factor added on web
 * is invisible there. The user would see different security settings depending
 * on which client they opened.
 *
 * **The body is deliberately empty.** The endpoint's own description says
 * client-reported fields "are not trusted" and that it validates against
 * Firebase Identity Platform server-side, so sending our view would be theatre.
 * This is a trigger, not a payload.
 *
 * **Never throws.** It runs after an enrolment that already succeeded, and a
 * failed sync is not a failed enrolment: surfacing it would tell the user their
 * factor did not take when it did.
 *
 * **It reports the outcome itself** rather than returning a boolean nobody
 * consumed — every call site was `void syncFactors()`, so the docstring's claim
 * that a caller could measure the divergence was untrue. A systematic split
 * between the web and iOS views of a user's factors is exactly the kind of
 * thing that is invisible until someone complains.
 *
 * When M13 adds `GET /mfa/status` this becomes that query key's invalidation
 * point rather than a bare call.
 */
export async function syncFactors(): Promise<boolean> {
  try {
    const { error } = await api.POST('/api/v1/mfa/sync-factors', { body: {} })
    if (error !== undefined) {
      console.warn('MFA factor sync failed; the backend may not list this factor yet.', error)
      emit('mfa_factor_sync', { result_status: 'failed' })
      return false
    }
    emit('mfa_factor_sync', { result_status: 'passed' })
    return true
  } catch (thrown) {
    console.warn('MFA factor sync could not be reached.', thrown)
    emit('mfa_factor_sync', { result_status: 'failed' })
    return false
  }
}

// ---------------------------------------------------------------------------
// The backend's `/mfa` reads and writes — M13 Security, hybrid (#189).
//
// Firebase holds the factors sign-in checks. These endpoints carry what the
// backend alone knows: recovery codes and the email-code factor, both used for
// step-up, never at sign-in.
// ---------------------------------------------------------------------------

type StatusWire = components['schemas']['MfaStatusResponse']
type CountWire = components['schemas']['BackupCodesCountResponse']
type RegenerateWire = components['schemas']['BackupCodesResponse']
type EmailSetupWire = components['schemas']['EmailOtpSetupResponse']

/**
 * Why a call failed, in the terms a screen branches on.
 *
 * - `reauth_required` — 403, the sign-in is older than five minutes: Firebase re-authentication fixes it.
 * - `step_up_required` / `step_up_invalid` — 403, the account has a factor the backend can challenge and
 *   needs an `X-Step-Up-Token`, or the one sent is spent or expired.
 * - `step_up_locked` — 429, too many wrong answers for now.
 * - `expired` — a step-up challenge that has timed out or is unknown.
 * - `invalid_code` — 400, a wrong or expired code or an unusable request.
 * - `otp_send_limited` — 429, too many email codes sent: `retryAfterSeconds` says how long to wait. No email went out.
 * - `rate_limited` — any other 429.
 * - `unavailable` — 5xx or a network failure.
 */
export type MfaErrorKind =
  | 'reauth_required'
  | 'step_up_required'
  | 'step_up_invalid'
  | 'step_up_locked'
  | 'invalid_code'
  | 'expired'
  | 'otp_send_limited'
  | 'rate_limited'
  | 'unavailable'
  | 'unknown'

export class MfaApiError extends Error {
  readonly kind: MfaErrorKind
  readonly status: number
  /** Only on `otp_send_limited`: whole seconds, always a positive finite integer. */
  readonly retryAfterSeconds: number | undefined
  constructor(kind: MfaErrorKind, status: number, message: string, retryAfterSeconds?: number) {
    super(message)
    this.retryAfterSeconds = retryAfterSeconds
    this.name = 'MfaApiError'
    this.kind = kind
    this.status = status
  }
}

/** The server's wait in seconds when `thrown` is a send limit, else null. The one reader of that error's shape. */
export function otpSendWait(thrown: unknown): number | null {
  return thrown instanceof MfaApiError && thrown.kind === 'otp_send_limited' ? (thrown.retryAfterSeconds ?? null) : null
}

/** True for the two answers that mean "do a step-up and try again". */
export function needsStepUp(thrown: unknown): boolean {
  return (
    thrown instanceof MfaApiError &&
    (thrown.kind === 'step_up_required' || thrown.kind === 'step_up_invalid')
  )
}

/** Used when a send limit arrives with neither a usable body field nor a usable `Retry-After` header. Matches the backend's per-purpose window. */
export const DEFAULT_OTP_RETRY_SECONDS = 60
/** An hour is the longest window the backend has (10 sends per user per hour); anything beyond is treated as malformed. */
const MAX_OTP_RETRY_SECONDS = 3600

/** A positive whole number of seconds within the cap, or null. Rejects NaN, 0, negatives, non-numeric strings and anything over the cap; a fraction rounds UP (0.2 is 1 second), never to zero. */
function waitSeconds(value: unknown): number | null {
  const n = typeof value === 'string' && /^\s*\d+\s*$/.test(value) ? Number(value) : value
  if (typeof n !== 'number' || !Number.isFinite(n)) return null
  const whole = Math.ceil(n)
  return whole >= 1 && whole <= MAX_OTP_RETRY_SECONDS ? whole : null
}

const GATES: ReadonlySet<string> = new Set([
  'otp_send_limited',
  'reauth_required',
  'step_up_required',
  'step_up_invalid',
  'step_up_locked',
])

/** FastAPI's `detail` is a string for most errors and `{status, message}` for the gates (step-up, re-auth, send limit). */
function describe(body: unknown): { gate: string | null; message: string | null; retryAfter: unknown } {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return { gate: null, message: null, retryAfter: undefined }
  const { detail } = body as { detail: unknown }
  if (typeof detail === 'string') return { gate: null, message: detail, retryAfter: undefined }
  if (typeof detail === 'object' && detail !== null) {
    const { status, message, retry_after_seconds } = detail as {
      status?: unknown
      message?: unknown
      retry_after_seconds?: unknown
    }
    return {
      gate: typeof status === 'string' && GATES.has(status) ? status : null,
      message: typeof message === 'string' ? message : null,
      retryAfter: retry_after_seconds,
    }
  }
  return { gate: null, message: null, retryAfter: undefined }
}

function failure(status: number, body: unknown, retryAfterHeader: string | null = null): MfaApiError {
  const { gate, message, retryAfter } = describe(body)
  const text = message ?? 'The request could not be completed.'
  if (gate === 'otp_send_limited') {
    const wait = waitSeconds(retryAfter) ?? waitSeconds(retryAfterHeader) ?? DEFAULT_OTP_RETRY_SECONDS
    return new MfaApiError('otp_send_limited', status, text, wait)
  }
  if (gate !== null) return new MfaApiError(gate as MfaErrorKind, status, text)
  if (status === 429) return new MfaApiError('rate_limited', status, text)
  if (status === 400) return new MfaApiError('invalid_code', status, text)
  if (status >= 500) return new MfaApiError('unavailable', status, text)
  return new MfaApiError('unknown', status, text)
}

/** Unwraps an openapi-fetch result: the data, or a typed `MfaApiError`. */
function settle<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    throw failure(result.response.status, result.error, result.response.headers.get('Retry-After'))
  }
  return result.data
}

export type MfaStatus = { enabled: boolean; emailConfigured: boolean; backupCodesRemaining: number }

/** `GET /mfa/status`. The factors list is not read: Firebase is the screen's source for those. */
export async function getMfaStatus(): Promise<MfaStatus> {
  const data: StatusWire = settle(await api.GET('/api/v1/mfa/status'))
  return { enabled: data.is_enabled, emailConfigured: data.email_configured, backupCodesRemaining: data.backup_codes_remaining }
}

/** `GET /mfa/backup-codes` — a COUNT. The codes are only ever returned by regenerate, once. */
export async function getBackupCodesCount(): Promise<{ remaining: number; total: number }> {
  const data: CountWire = settle(await api.GET('/api/v1/mfa/backup-codes'))
  return { remaining: data.remaining, total: data.total }
}

/**
 * `POST /mfa/backup-codes/regenerate` — invalidates the old set and returns the
 * new codes. **The only moment they exist on the client**: callers hold them in
 * component state, never in the query cache.
 *
 * `stepUpToken` is sent as `X-Step-Up-Token` when the backend demands one.
 */
export async function regenerateBackupCodes(stepUpToken?: string): Promise<string[]> {
  const data: RegenerateWire = settle(
    await api.POST('/api/v1/mfa/backup-codes/regenerate', {
      params: { header: stepUpToken === undefined ? {} : { 'X-Step-Up-Token': stepUpToken } },
    }),
  )
  return data.codes
}

/** `POST /mfa/setup/email` — sends a code to the account email. Gated like any factor change. */
export async function startEmailSetup(
  stepUpToken?: string,
): Promise<{ maskedEmail: string; expiresInSeconds: number }> {
  const data: EmailSetupWire = settle(
    await api.POST('/api/v1/mfa/setup/email', {
      params: { header: stepUpToken === undefined ? {} : { 'X-Step-Up-Token': stepUpToken } },
    }),
  )
  return { maskedEmail: data.masked_email, expiresInSeconds: data.expires_in_seconds }
}

/** `POST /mfa/setup/email/verify`. A wrong or expired code is `invalid_code`. */
export async function verifyEmailSetup(code: string): Promise<void> {
  settle(await api.POST('/api/v1/mfa/setup/email/verify', { body: { code } }))
}

/**
 * `POST /mfa/resend/email`. With no argument it resends the ENROLMENT code; with
 * a step-up `challengeToken` it sends that challenge's code to the account email.
 */
export async function resendEmailCode(challengeToken?: string): Promise<void> {
  settle(
    await api.POST('/api/v1/mfa/resend/email', {
      body: challengeToken === undefined ? {} : { challenge_token: challengeToken },
    }),
  )
}

type StepUpWire = components['schemas']['StepUpResponse']
type StepUpVerifyWire = components['schemas']['StepUpVerifyResponse']

export type StepUpChallenge = {
  /** Also the `X-Step-Up-Token` once verified. Single use, valid 5 minutes after approval. Never stored or logged. */
  challengeToken: string
  /** The one factor the backend wants: `email` and `totp` can be answered on web. */
  required: string
  expiresAt: Date
}

/** `POST /mfa/step-up/request` for a factor change (`mfa_change`). */
export async function requestStepUp(): Promise<StepUpChallenge> {
  const data: StepUpWire = settle(
    await api.POST('/api/v1/mfa/step-up/request', { body: { action: 'mfa_change' } }),
  )
  return {
    challengeToken: data.challenge_token,
    required: data.required_factors[0] ?? 'totp',
    expiresAt: new Date(data.expires_at),
  }
}

/** What a step-up code can be: the emailed code, the backend authenticator's, or an unused recovery code. */
export type StepUpMethod = 'email' | 'totp' | 'backup-code'

/**
 * `POST /mfa/step-up/verify`. True when every required factor is done, i.e. the
 * challenge token may now be sent as `X-Step-Up-Token`.
 */
export async function verifyStepUp(
  challengeToken: string,
  method: StepUpMethod,
  code: string,
): Promise<boolean> {
  try {
    const data: StepUpVerifyWire = settle(
      await api.POST('/api/v1/mfa/step-up/verify', {
        body: { challenge_token: challengeToken, method, code },
      }),
    )
    return data.fully_verified
  } catch (thrown) {
    if (thrown instanceof MfaApiError) {
      if (thrown.status === 404 || (thrown.status === 400 && /expired/i.test(thrown.message))) {
        throw new MfaApiError('expired', thrown.status, thrown.message)
      }
    }
    throw thrown
  }
}
